import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState, useEffect } from "react";
import { FileEdit } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";
import {
  getCountries,
  getStatesByCountry,
  getCitiesByState,
} from "helpers/location_data";

interface Option {
  value: string;
  label: string;
}

interface propTypes {
  handleRefresh: () => void;
  toggle: () => void;
  show: boolean;
  editData: any;
}

const SubAccountEditModal = ({
  handleRefresh,
  show,
  toggle,
  editData,
}: propTypes) => {
  const AppService = useAppServices();
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<Option[]>([]);
  const [states, setStates] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);

  // ✅ Fetch countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      setLoading(true);
      const list = await getCountries();
      setCountries(list.map((c) => ({ value: c.name, label: c.name })));
      setLoading(false);
    };
    loadCountries();
  }, []);

  // ✅ Submit handler
  const handleSubmit = async (values: any) => {
    const payload = { ...values, _id: editData._id };
    const { response } = await AppService.sub_account.update({
      payload,
      toaster: true,
    });
    if (response) {
      toggle();
      handleRefresh();
    }
  };

  // ✅ Formik + Yup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editData?.name || "",
      email: editData?.email || "",
      phone: editData?.phone || "",
      website: editData?.website || "",
      address: editData?.address || "",
      country: editData?.country || "",
      state: editData?.state || "",
      city: editData?.city || "",
      zip: editData?.zip || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
      email: Yup.string().email("Invalid email").required("Please enter email"),
      phone: Yup.string().required("Please enter phone"),
      website: Yup.string().url("Invalid website URL").nullable(),
      address: Yup.string().required("Please enter address"),
      country: Yup.string().required("Please select a country"),
      state: Yup.string().nullable(),
      city: Yup.string().nullable(),
      zip: Yup.string().required("Please enter ZIP code"),
    }),
    onSubmit: handleSubmit,
  });
  // ✅ Fetch states when country changes
  useEffect(() => {
    if (!formik.values.country) return;
    setStates([]);
    setCities([]);
    const loadStates = async () => {
      setLoading(true);
      const list = await getStatesByCountry(formik.values.country);
      setStates(list.map((s) => ({ value: s.name, label: s.name })));
      setLoading(false);
    };
    loadStates();
  }, [formik.values.country]);

  // ✅ Fetch cities when state changes
  useEffect(() => {
    if (!formik.values.state || !formik.values.country) return;
    const loadCities = async () => {
      setLoading(true);
      const list = await getCitiesByState(
        formik.values.country,
        formik.values.state
      );
      setCities(list.map((c) => ({ value: c.name, label: c.name })));
      setLoading(false);
    };
    loadCities();
  }, [formik.values.state]);

  return (
    <Modal
      show={show}
      onHide={toggle}
      id="subAccountModal"
      modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
    >
      <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
        <Modal.Title className="text-16 font-semibold flex items-center gap-2">
          <FileEdit size={18} /> Edit SubAccount
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <InputField label="Name" name="name" formik={formik} />
          <InputField label="Email" name="email" type="email" formik={formik} />
          <InputField label="Phone" name="phone" formik={formik} />
          <InputField
            label="Website"
            name="website"
            placeholder="https://example.com"
            formik={formik}
          />
          <InputField label="Address" name="address" formik={formik} />

          <SelectField
            label="Country"
            name="country"
            options={countries}
            formik={formik}
            isLoading={loading}
          />

          <SelectField
            label="State"
            name="state"
            options={states}
            formik={formik}
            isLoading={loading}
            isDisabled={!formik.values.country}
          />

          <SelectField
            label="City"
            name="city"
            options={cities}
            formik={formik}
            isLoading={loading}
            isDisabled={!formik.values.state}
          />

          <InputField label="ZIP" name="zip" formik={formik} />

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="text-red-500 bg-white border btn hover:text-red-600"
              onClick={() => {
                formik.resetForm();
                toggle();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
            >
              Update SubAccount
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default SubAccountEditModal;

/* ✅ Reusable Input & Select components */
interface InputProps {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  formik: any;
}

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  formik,
}: InputProps) => (
  <div>
    <label className="block mb-1 text-sm font-medium">{label}</label>
    <input
      type={type}
      name={name}
      placeholder={placeholder ?? `Enter ${label.toLowerCase()}`}
      className="form-input w-full"
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      value={formik.values[name]}
    />
    {formik.touched[name] && formik.errors[name] && (
      <p className="text-red-400 text-sm mt-1">{formik.errors[name]}</p>
    )}
  </div>
);

interface SelectProps {
  label: string;
  name: string;
  options: Option[];
  formik: any;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const SelectField = ({
  label,
  name,
  options,
  formik,
  isLoading,
  isDisabled,
}: SelectProps) => (
  <div>
    <label className="block mb-1 text-sm font-medium">{label}</label>
    <Select
      name={name}
      options={options}
      placeholder={`Select ${label.toLowerCase()}`}
      isLoading={isLoading}
      isDisabled={isDisabled}
      value={
        formik.values[name]
          ? { value: formik.values[name], label: formik.values[name] }
          : null
      }
      onChange={(opt: Option | null) =>
        formik.setFieldValue(name, opt?.value ?? "")
      }
      onBlur={() => formik.setFieldTouched(name, true)}
    />
    {formik.touched[name] && formik.errors[name] && (
      <p className="text-red-400 text-sm mt-1">{formik.errors[name]}</p>
    )}
  </div>
);
